import { CartUpdatedEvent } from '@theme/events';

class CartBenefits extends HTMLElement {
  #recommendations = null;
  #controller = null;
  #busy = false;
  #requiresReview = false;
  #slideIndex = 0;
  #animations = new Set();

  connectedCallback() {
    this.#controller = new AbortController();
    const drawer = this.closest('cart-drawer');
    const body = drawer?.querySelector('.cart-drawer__body');
    const template = this.querySelector('[data-benefits-recommendations]');
    if (!body || !template) return;

    this.#recommendations = template.content.firstElementChild.cloneNode(true);
    body.append(this.#recommendations);
    const options = { signal: this.#controller.signal };
    this.#recommendations.addEventListener('submit', this.#add, options);
    this.#recommendations.addEventListener('change', this.#changeVariant, options);
    this.#recommendations.addEventListener('click', (event) => {
      if (event.target.closest('[data-benefits-next]')) this.#move(1);
      if (event.target.closest('[data-benefits-previous]')) this.#move(-1);
    }, options);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#animations.forEach((animation) => animation.cancel());
    this.#recommendations?.remove();
  }

  getAnimationState() {
    const drawer = this.closest('cart-drawer');
    const shipping = this.querySelector('.cart-benefits__shipping');
    const surface = shipping?.querySelector('.cart-benefits__shipping-surface');
    return {
      visible: drawer?.classList.contains('is-open'),
      reached: [...this.querySelectorAll('.cart-benefits__step')].map((step) => step.classList.contains('is-reached')),
      headline: this.querySelector('.cart-benefits__headline')?.textContent.trim(),
      bodyTop: drawer?.querySelector('.cart-drawer__body')?.getBoundingClientRect().top,
      shipping: shipping ? {
        complete: shipping.classList.contains('is-complete'),
        progress: Number(shipping.querySelector('[role="progressbar"]')?.getAttribute('aria-valuenow') ?? 100),
        width: shipping.getBoundingClientRect().width,
        height: shipping.getBoundingClientRect().height,
        background: getComputedStyle(surface || shipping).backgroundColor,
      } : null,
    };
  }

  animateUpdate(previous) {
    if (!previous?.visible) return;
    const current = this.getAnimationState();
    const headline = this.querySelector('.cart-benefits__headline');
    const styles = getComputedStyle(this);
    const easing = styles.getPropertyValue('--animation-easing-out').trim() || 'cubic-bezier(0.23, 1, 0.32, 1)';
    const duration = Math.min(parseFloat(styles.getPropertyValue('--animation-speed')) * 1000 || 250, 300);
    const fast = Math.min(parseFloat(styles.getPropertyValue('--animation-speed-medium')) * 1000 || 150, 200);
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (previous.headline !== current.headline) {
      this.#play(headline, [{ opacity: 0.6 }, { opacity: 1 }], { duration: fast, easing });
    }
    if (reducedMotion) return;

    this.querySelectorAll('.cart-benefits__step').forEach((step, index) => {
      if (previous.reached[index] === current.reached[index]) return;
      const marker = step.querySelector('.cart-benefits__marker');
      const gift = step.classList.contains('cart-benefits__step--gift');
      this.#play(marker, [
        { transform: 'scale(0.92)', opacity: 0.55 },
        { transform: 'scale(1)', opacity: 1 },
      ], { duration: gift ? duration : fast, easing });
    });

    const shipping = this.querySelector('.cart-benefits__shipping');
    if (shipping && previous.shipping && current.shipping) {
      const fill = shipping.querySelector('[data-benefits-shipping-fill]');
      if (fill && previous.shipping.progress !== current.shipping.progress) {
        this.#play(fill, [
          { transform: `scaleX(${previous.shipping.progress / 100})` },
          { transform: `scaleX(${current.shipping.progress / 100})` },
        ], { duration, easing });
      }
      if (previous.shipping.complete !== current.shipping.complete) {
        const surface = shipping.querySelector('.cart-benefits__shipping-surface');
        const scaleX = current.shipping.width > 0 ? previous.shipping.width / current.shipping.width : 1;
        const scaleY = current.shipping.height > 0 ? previous.shipping.height / current.shipping.height : 1;
        const start = `scale(${scaleX}, ${scaleY})`;
        this.#play(surface, [{ transform: start }, { transform: 'scale(1)' }], { duration, easing });
        const oldSurface = surface.cloneNode();
        oldSurface.style.background = previous.shipping.background;
        shipping.append(oldSurface);
        this.#play(oldSurface, [
          { transform: start, opacity: 1 },
          { transform: 'scale(1)', opacity: 0 },
        ], { duration, easing }, () => oldSurface.remove());
        this.#play(shipping.querySelector('p'), [{ opacity: 0.5 }, { opacity: 1 }], { duration: fast, easing });
      }
    }

    const body = this.closest('cart-drawer')?.querySelector('.cart-drawer__body');
    const offset = previous.bodyTop - current.bodyTop;
    if (body && Number.isFinite(offset) && Math.abs(offset) > 1) {
      this.#play(body, [{ transform: `translateY(${offset}px)` }, { transform: 'translateY(0)' }], { duration, easing });
    }
  }

  #play(element, keyframes, options, cleanup) {
    if (!element?.animate) {
      cleanup?.();
      return;
    }
    const animation = element.animate(keyframes, options);
    this.#animations.add(animation);
    const finish = () => {
      this.#animations.delete(animation);
      cleanup?.();
    };
    animation.onfinish = finish;
    animation.oncancel = finish;
  }

  #move(direction) {
    if (this.#busy) return;
    const slides = [...this.#recommendations.querySelectorAll('[data-benefits-slide]')];
    if (slides.length < 2) return;
    this.#slideIndex = (this.#slideIndex + direction + slides.length) % slides.length;
    slides.forEach((slide, index) => { slide.hidden = index !== this.#slideIndex; });
    this.#recommendations.querySelector('[data-benefits-slide-status]').textContent = slides[this.#slideIndex].getAttribute('aria-label');
  }

  #changeVariant = (event) => {
    const select = event.target.closest('[data-benefits-variant]');
    if (!select) return;
    const form = select.closest('[data-benefits-slide]');
    const selected = select.selectedOptions[0];
    form.querySelector('[data-benefits-price]').textContent = selected.dataset.price;
    const image = form.querySelector('img');
    if (image && selected.dataset.image) {
      image.srcset = '';
      image.src = selected.dataset.image;
    }
    const url = new URL(form.dataset.productUrl, window.location.origin);
    url.searchParams.set('variant', select.value);
    form.querySelectorAll('a[href]').forEach((link) => { link.href = url.href; });
  };

  #add = async (event) => {
    const form = event.target.closest('[data-benefits-add]');
    if (!form) return;
    event.preventDefault();
    if (this.#busy || this.#requiresReview) return;

    const drawer = this.closest('cart-drawer');
    const variantId = Number(new FormData(form).get('id'));
    const errorElement = this.#recommendations.querySelector('[role="alert"]');
    const controls = [...drawer.querySelectorAll('button, input, select')].filter((control) => !control.disabled);
    this.#busy = true;
    this.#recommendations.setAttribute('aria-busy', 'true');
    form.classList.add('is-pending');
    errorElement.hidden = true;
    controls.forEach((control) => { control.disabled = true; });
    let added = false;
    let feedbackMessage = this.dataset.errorMessage;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: [drawer.dataset.sectionId, 'auto-gifts'],
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (!response.ok || data.status || data.errors) {
        if (typeof data.description === 'string') feedbackMessage = data.description;
        throw new Error(feedbackMessage);
      }
      added = true;
      if (!data.sections?.[drawer.dataset.sectionId]) {
        const sectionUrl = new URL(window.location.href);
        sectionUrl.searchParams.set('sections', [drawer.dataset.sectionId, 'auto-gifts'].join(','));
        const sectionResponse = await fetch(sectionUrl);
        if (!sectionResponse.ok) throw new Error(feedbackMessage);
        data.sections = await sectionResponse.json();
      }
      if (!data.sections?.[drawer.dataset.sectionId]) throw new Error(feedbackMessage);

      const html = new DOMParser().parseFromString(data.sections[drawer.dataset.sectionId], 'text/html');
      const count = Number(html.querySelector('cart-benefits')?.dataset.cartCount);
      const rendered = new Promise((resolve) => {
        drawer.addEventListener('cart:rendered', () => {
          drawer.querySelector('.cart-benefits__headline')?.focus({ preventScroll: true });
          resolve();
        }, { once: true });
      });
      document.dispatchEvent(new CartUpdatedEvent({}, {
        source: 'cart-benefits',
        itemCount: Number.isFinite(count) ? count : undefined,
        sections: data.sections,
      }));
      await rendered;
    } catch {
      if (this.isConnected) {
        errorElement.textContent = feedbackMessage;
        errorElement.hidden = false;
        this.#requiresReview = added;
        this.#recommendations.querySelector('[data-benefits-review]').hidden = false;
      }
    } finally {
      this.#busy = false;
      this.#recommendations?.removeAttribute('aria-busy');
      form.classList.remove('is-pending');
      controls.forEach((control) => { if (control.isConnected) control.disabled = false; });
      if (this.#requiresReview) this.#recommendations?.querySelectorAll('[type="submit"]').forEach((control) => { control.disabled = true; });
    }
  };
}

if (!customElements.get('cart-benefits')) customElements.define('cart-benefits', CartBenefits);
